import React, { useEffect, useRef, useState } from "react";
import darkLoader from "../assests/bg-black-loader.mp4";
import lightLoader from "../assests/loader.mp4";

interface VideoLoaderProps {
  size?: "small" | "medium";
}

// Safari only streams <video src> from servers that answer byte-range requests
// (206). Many CDNs/proxies don't, so we download the whole file once and play
// it from a blob URL, which works everywhere. Cached per source for the session.
const blobCache: Record<string, Promise<string>> = {};
const resolvedUrls: Record<string, string> = {};

const getBlobUrl = (src: string): Promise<string> => {
  if (!blobCache[src]) {
    blobCache[src] = fetch(src)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to load ${src}`);
        return res.blob();
      })
      .then((blob) => {
        const url = URL.createObjectURL(new Blob([blob], { type: "video/mp4" }));
        resolvedUrls[src] = url;
        return url;
      })
      .catch((err) => {
        delete blobCache[src];
        throw err;
      });
  }
  return blobCache[src];
};

// Start downloading as soon as the app loads so the video is usually ready
// before any loader is shown.
const isDarkNow = () => document.documentElement.classList.contains("theme-dark");
getBlobUrl(isDarkNow() ? darkLoader : lightLoader).catch(() => {});

export default function VideoLoader({ size = "medium" }: VideoLoaderProps) {
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("theme-dark")
  );
  const [videoUrl, setVideoUrl] = useState<string | null>(
    () => resolvedUrls[isDark ? darkLoader : lightLoader] ?? null
  );
  const [showSpinner, setShowSpinner] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("theme-dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const src = isDark ? darkLoader : lightLoader;
    setVideoUrl(resolvedUrls[src] ?? null);
    getBlobUrl(src)
      .then((url) => {
        if (!cancelled) setVideoUrl(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isDark]);

  // Only show the fallback spinner if the video is taking noticeably long,
  // so a fast load doesn't flash a spinner before the video.
  useEffect(() => {
    if (videoUrl) {
      setShowSpinner(false);
      return;
    }
    const t = setTimeout(() => setShowSpinner(true), 700);
    return () => clearTimeout(t);
  }, [videoUrl]);

  // React doesn't render `muted` as an HTML attribute, and Safari refuses to
  // autoplay unless it's really present. Set it manually and play explicitly.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !videoUrl) return;
    video.muted = true;
    video.defaultMuted = true;
    video.setAttribute("muted", "");
    video.setAttribute("playsinline", "");
    const tryPlay = () => {
      const p = video.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };
    tryPlay();
    video.addEventListener("canplay", tryPlay);
    return () => video.removeEventListener("canplay", tryPlay);
  }, [videoUrl]);

  const dimension = size === "small" ? 48 : 120;

  if (!videoUrl) {
    // Reserve the space so nothing shifts when the video appears.
    if (!showSpinner) return <div style={{ width: dimension, height: dimension }} />;
    return (
      <div
        role="status"
        aria-label="Loading"
        style={{
          width: dimension / 2,
          height: dimension / 2,
          margin: dimension / 4,
          border: `${size === "small" ? 3 : 4}px solid rgba(128,128,128,0.3)`,
          borderTopColor: isDark ? "#fff" : "#000",
          borderRadius: "50%",
          animation: "videoLoaderSpin 0.8s linear infinite",
        }}
      >
        <style>{`@keyframes videoLoaderSpin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      key={videoUrl}
      src={videoUrl}
      autoPlay
      loop
      muted
      playsInline
      preload="auto"
      disablePictureInPicture
      width={dimension}
      height={dimension}
      style={{ display: "block" }}
    />
  );
}
